import { CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import "./SeriousAlert.scss";

interface SeriousAlertProps {
  /** What happened, in plain words. */
  title: string;
  /** What it means and what to do. */
  children?: ReactNode;
  /** Recovery actions, for example Retry payment and Update card. */
  actions?: ReactNode;
  /** Makes the alert dismissible: a close button in the top-right corner, after the actions in tab order. */
  onClose?: () => void;
  /** The close button's accessible name; "Close" in the user's language by default. */
  closeLabel?: string;
  /** Extra classes on the root, for the caller's spacing. */
  className?: string;
}

/** The Serious level: payment failures, stream errors, anything about money or data. */
export const SeriousAlert = ({ title, children, actions, onClose, closeLabel, className }: SeriousAlertProps) => {
  const { t } = useTranslation("common");
  const classes = ["st-serious", "st-level-serious", onClose ? "st-serious--closable" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={classes} role="alert">
      <span className="st-serious__icon" aria-hidden="true">
        <ExclamationCircleOutlined />
      </span>
      <div className="st-serious__body">
        <div className="st-serious__title">{title}</div>
        {children ? <div className="st-serious__text">{children}</div> : null}
      </div>
      {actions ? <div className="st-serious__actions">{actions}</div> : null}
      {onClose ? (
        <Button
          type="text"
          size="small"
          className="st-serious__close"
          icon={<CloseOutlined />}
          aria-label={closeLabel ?? t("actions.close")}
          onClick={onClose}
        />
      ) : null}
    </div>
  );
};

export default SeriousAlert;
